<template>
  <div class="scoring-panel-container">
    <!-- Header with title and Edit/Save button -->
    <div class="scoring-header d-flex justify-content-between align-items-center mb-3">
      <h6 class="panel-title mb-0">Scoring</h6>
      <div class="button-group">
        <b-button 
          v-if="editMode"
          variant="outline-secondary" 
          size="sm"
          @click="cancelChanges"
          :disabled="saving"
        >
          Cancel
        </b-button>
        <b-button 
          v-if="editMode"
          variant="outline-danger" 
          size="sm"
          @click="clearScore"
          :disabled="saving"
        >
          Clear Score
        </b-button>
        <b-button 
          :variant="editMode ? 'success' : 'outline-primary'" 
          size="sm"
          @click="toggleEditMode"
          :disabled="loading || saving"
        >
          {{ saving ? 'Saving...' : (editMode ? 'Save' : 'Edit') }}
        </b-button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="text-center py-4">
      <!-- <b-spinner small></b-spinner> -->
      <span class="ml-2">Loading...</span>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Scoring form -->
    <div v-else class="scoring-form">
      <!-- Core Metrics Section -->
      <div class="section-header mb-2">
        <strong>Core Metrics</strong>
        <span class="text-muted small">(required)</span>
      </div>
      
      <div v-for="(fieldDef, fieldKey) in coreMetricsSchema" :key="'core-' + fieldKey" class="field-row" :class="{ 'missing-field': missingFields.includes(fieldKey) }">
        <div class="field-label">
          <span>{{ formatLabel(fieldKey) }}</span>
          <span 
            class="tooltip-icon" 
            v-b-tooltip.hover 
            :title="fieldDef.description || 'No description'"
          >?</span>
        </div>
        <div class="field-input">
          <!-- Integer with range -->
          <template v-if="fieldDef.type === 'integer' && fieldDef.minimum !== undefined && fieldDef.maximum !== undefined">
            <div class="range-container">
              <input 
                type="range" 
                :min="fieldDef.minimum" 
                :max="fieldDef.maximum"
                v-model.number="formData.core_metrics[fieldKey]"
                :disabled="!editMode"
                class="form-range"
              />
              <span class="range-value">{{ formData.core_metrics[fieldKey] }}</span>
            </div>
          </template>
          
          <!-- Integer without range (just number input) -->
          <template v-else-if="fieldDef.type === 'integer'">
            <input 
              type="number" 
              :min="fieldDef.minimum || 0"
              v-model.number="formData.core_metrics[fieldKey]"
              :disabled="!editMode"
              class="form-control form-control-sm"
            />
          </template>
          
          <!-- Boolean -->
          <template v-else-if="fieldDef.type === 'boolean'">
            <div class="toggle-boxes">
              <div 
                class="toggle-box" 
                :class="{ selected: formData.core_metrics[fieldKey] === true, disabled: !editMode }"
                @click="editMode && setBoolean('core_metrics', fieldKey, true)"
              >True</div>
              <div 
                class="toggle-box" 
                :class="{ selected: formData.core_metrics[fieldKey] === false, disabled: !editMode }"
                @click="editMode && setBoolean('core_metrics', fieldKey, false)"
              >False</div>
            </div>
          </template>
        </div>
      </div>

      <!-- Description Section -->
      <div class="section-header mt-4 mb-2">
        <strong>Description</strong>
        <span class="text-muted small">(optional)</span>
      </div>

      <div v-for="(fieldDef, fieldKey) in descriptionMetricsSchema" :key="'description-' + fieldKey" class="field-row">
        <div class="field-label">
          <span>{{ formatLabel(fieldKey) }}</span>
          <span
            class="tooltip-icon"
            v-b-tooltip.hover
            :title="fieldDef.description || 'No description'"
          >?</span>
        </div>
        <div class="field-input">
          <template v-if="fieldDef.enum">
            <div class="toggle-boxes">
              <div
                v-for="opt in fieldDef.enum"
                :key="opt"
                class="toggle-box"
                :class="{ selected: formData.description_metrics[fieldKey] === opt, disabled: !editMode }"
                @click="editMode && setEnum('description_metrics', fieldKey, opt)"
              >{{ opt }}</div>
            </div>
          </template>
        </div>
      </div>

      <!-- Optional Attributes Section -->
      <div class="section-header mt-4 mb-2 d-flex justify-content-between align-items-center">
        <div>
          <strong>Optional Attributes</strong>
          <span class="text-muted small">(add as needed)</span>
        </div>
        <!-- Add criteria dropdown -->
        <b-dropdown 
          v-if="editMode && availableOptionalAttributes.length > 0"
          text="+ Add" 
          size="sm" 
          variant="outline-secondary"
          right
        >
          <b-dropdown-item 
            v-for="attr in availableOptionalAttributes" 
            :key="attr"
            @click="addOptionalAttribute(attr)"
          >
            {{ formatLabel(attr) }}
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <!-- Active optional attributes -->
      <div v-for="fieldKey in activeOptionalAttributes" :key="'opt-' + fieldKey" class="field-row">
        <div class="field-label">
          <span>{{ formatLabel(fieldKey) }}</span>
          <span 
            class="tooltip-icon" 
            v-b-tooltip.hover 
            :title="(optionalAttributesSchema[fieldKey] && optionalAttributesSchema[fieldKey].description) || 'No description'"
          >?</span>
          <!-- Remove button -->
          <span 
            v-if="editMode" 
            class="remove-icon" 
            @click="removeOptionalAttribute(fieldKey)"
            title="Remove"
          >&times;</span>
        </div>
        <div class="field-input">
          <!-- Enum (True/False/Missing) -->
          <template v-if="optionalAttributesSchema[fieldKey] && optionalAttributesSchema[fieldKey].enum">
            <div class="toggle-boxes">
              <div 
                v-for="opt in optionalAttributesSchema[fieldKey].enum" 
                :key="opt"
                class="toggle-box" 
                :class="{ selected: formData.optional_attributes[fieldKey] === opt, disabled: !editMode }"
                @click="editMode && setEnum('optional_attributes', fieldKey, opt)"
              >{{ opt }}</div>
            </div>
          </template>
          
          <!-- Integer -->
          <template v-else-if="optionalAttributesSchema[fieldKey] && optionalAttributesSchema[fieldKey].type === 'integer'">
            <input 
              type="number" 
              :min="(optionalAttributesSchema[fieldKey] && optionalAttributesSchema[fieldKey].minimum) || 0"
              v-model.number="formData.optional_attributes[fieldKey]"
              :disabled="!editMode"
              class="form-control form-control-sm"
            />
          </template>
          
          <!-- String -->
          <template v-else>
            <input 
              type="text"
              v-model="formData.optional_attributes[fieldKey]"
              :disabled="!editMode"
              class="form-control form-control-sm"
            />
          </template>
        </div>
      </div>

      <div v-if="activeOptionalAttributes.length === 0" class="text-muted small py-2">
        No optional attributes added. Click "+ Add" to include additional criteria.
      </div>

      <!-- Evaluator Comments -->
      <div class="section-header mt-4 mb-2">
        <strong>Comments</strong>
        <span class="text-muted small">(optional)</span>
      </div>
      <textarea 
        v-model="formData.evaluator_comments"
        :disabled="!editMode"
        class="form-control"
        rows="3"
        maxlength="500"
        placeholder="Notes on edge cases or issues during comparison..."
      ></textarea>
      <small class="text-muted">{{ (formData.evaluator_comments || '').length }}/500</small>

      <!-- Save error message -->
      <div v-if="saveError" class="alert alert-danger mt-3">
        {{ saveError }}
      </div>
    </div>
  </div>
</template>

<script>
import service from '../../services/llm-comparison'

/**
 * Static Scoring Panel Component
 * 
 * Form fields are statically defined based on scoring.schema.json.
 * See: cdk/appconfig/scoring.schema.json for the source schema definition.
 */
export default {
  name: 'ScoringPanel',
  props: {
    jobId: {
      type: String,
      required: true
    },
    model: {
      type: String,
      required: true
    },
    s3Key: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      saveError: null,
      editMode: false,
      // Static field definitions from scoring.schema.json
      coreMetricsFields: [
        {
          key: 'overall_extraction_quality',
          type: 'integer',
          minimum: 1,
          maximum: 10,
          description: 'Rate the overall quality of the extraction (1-10, where 10 is perfect)'
        },
        {
          key: 'actions_summarized',
          type: 'boolean',
          description: 'Is action info summarized and missing details? (True/False)'
        },
        {
          key: 'risk_summarized',
          type: 'boolean',
          description: 'Is risk to health info summarized and missing details? (True/False)'
        },
        {
          key: 'description_summarized',
          type: 'boolean',
          description: 'Is description info summarized and missing details? (True/False)'
        },
        {
          key: 'incorrect_fields_count',
          type: 'integer',
          minimum: 0,
          description: 'Number of fields extracted incorrectly'
        },
        {
          key: 'products_extracted_count',
          type: 'integer',
          minimum: 0,
          description: 'Total products extracted'
        },
        {
          key: 'total_products_count',
          type: 'integer',
          minimum: 0,
          description: 'Total number of products'
        },
        {
          key: 'duplicate_fields_correct',
          type: 'boolean',
          description: 'Are Recall Name, Recall Date and Recalling Firm extracted correctly? True if all 3 are correct, False if any are incorrect or missing.'
        }
      ],
      optionalAttributesFields: {
        udi_number: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'UDI number or product model extracted/matched correctly overall'
        },
        product_names: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Product names extracted/matched correctly'
        },
        product_extraction_count: {
          type: 'integer',
          minimum: 0,
          description: 'Total products extracted (document-wide)'
        },
        item_number: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Item number extracted/matched correctly'
        },
        material_number: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Material number extracted/matched correctly'
        },
        ndc: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'NDC (National Drug Code) extracted/matched correctly overall'
        },
        dist_start_end_dates: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Distribution start/end dates extracted/matched'
        },
        exp_start_end_dates: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Expiration start/end dates extracted/matched'
        },
        lot_numbers: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Lot numbers extracted/matched correctly'
        },
        serial_number: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Serial number extracted/matched correctly'
        },
        part_number: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Part number extracted/matched correctly'
        },
        product_table_overall: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Overall product table attributes captured accurately (structure, rows, key cells like item/part)'
        },
        recall_id: {
          type: 'enum',
          enum: ['True', 'False', 'Missing'],
          description: 'Recall ID extracted/matched correctly'
        }
      },
      descriptionMetricsFields: {
        product_name_verbatim: {
          type: 'enum',
          enum: ['True', 'False', 'Not in PDF'],
          description: 'Whether product name in description is verbatim from source PDF'
        },
        product_defect_verbatim: {
          type: 'enum',
          enum: ['True', 'False', 'Not in PDF'],
          description: 'Whether product defect details in description are verbatim from source PDF'
        },
        product_discovery_verbatim: {
          type: 'enum',
          enum: ['True', 'False', 'Not in PDF'],
          description: 'Whether product discovery details in description are verbatim from source PDF'
        },
        description_quality: {
          type: 'enum',
          enum: ['Repetitive', 'Summarized', 'Correct', 'Hallucinated', 'Extra-Information'],
          description: 'Quality rating for extracted description'
        }
      },
      formData: {
        core_metrics: {},
        description_metrics: {},
        optional_attributes: {},
        evaluator_comments: ''
      },
      activeOptionalAttributes: [],
      existingScore: null,
      originalFormData: null,
      originalActiveOptionalAttributes: [],
      missingFields: []
    }
  },
  computed: {
    coreMetricsSchema() {
      // Convert array to schema-like object for template compatibility
      const schema = {}
      for (const field of this.coreMetricsFields) {
        schema[field.key] = field
      }
      return schema
    },
    optionalAttributesSchema() {
      return this.optionalAttributesFields
    },
    descriptionMetricsSchema() {
      return this.descriptionMetricsFields
    },
    allOptionalAttributeKeys() {
      return Object.keys(this.optionalAttributesFields)
    },
    availableOptionalAttributes() {
      return this.allOptionalAttributeKeys.filter(k => !this.activeOptionalAttributes.includes(k))
    }
  },
  watch: {
    model: {
      immediate: false,
      handler() {
        this.loadData()
      }
    },
    jobId: {
      immediate: false,
      handler() {
        this.loadData()
      }
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.error = null
      this.editMode = false
      
      try {
        // Initialize form data with defaults
        this.initializeFormData()

        // Try to load existing score - 404 is expected if no score exists yet
        try {
          const scoreResp = await service.getScore(this.jobId, this.model)
          const scores = scoreResp.data || scoreResp.body || scoreResp
          if (scores && Array.isArray(scores) && scores.length > 0) {
            const score = scores[0]  // Take the first (and should be only) score
            this.existingScore = score
            this.populateFromExistingScore(score)
          } else {
            this.existingScore = null
          }
        } catch (e) {
          // 404 means no score exists yet - this is normal, not an error
          if (e.response && e.response.status === 404) {
            // No existing score - form will show defaults
            this.existingScore = null
          } else {
            // Actual error loading score
            console.error('Error loading score:', e)
            throw e
          }
        }
      } catch (e) {
        console.error('Error initializing scoring panel:', e)
        this.error = 'Failed to load scoring panel'
      } finally {
        this.loading = false
      }
    },

    initializeFormData() {
      // Initialize core_metrics with defaults from static field definitions
      const coreDefaults = {}
      for (const field of this.coreMetricsFields) {
        if (field.type === 'integer') {
          coreDefaults[field.key] = field.minimum || 0
        } else if (field.type === 'boolean') {
          coreDefaults[field.key] = null
        } else {
          coreDefaults[field.key] = null
        }
      }
      this.formData.core_metrics = coreDefaults

      const descriptionDefaults = {}
      for (const fieldKey of Object.keys(this.descriptionMetricsFields)) {
        descriptionDefaults[fieldKey] = null
      }
      this.formData.description_metrics = descriptionDefaults

      this.formData.optional_attributes = {}
      this.formData.evaluator_comments = ''
      this.activeOptionalAttributes = []
    },

    populateFromExistingScore(score) {
      // Populate core_metrics
      if (score.core_metrics) {
        this.formData.core_metrics = { ...this.formData.core_metrics, ...score.core_metrics }
      }

      // Populate description_metrics
      if (score.description_metrics) {
        this.formData.description_metrics = { ...this.formData.description_metrics, ...score.description_metrics }
      }
      
      // Populate optional_attributes and track which are active
      if (score.optional_attributes) {
        this.formData.optional_attributes = { ...score.optional_attributes }
        this.activeOptionalAttributes = Object.keys(score.optional_attributes)
      }
      
      // Populate comments
      if (score.evaluator_comments) {
        this.formData.evaluator_comments = score.evaluator_comments
      }

      // Store original state for change detection
      this.storeOriginalState()
    },

    storeOriginalState() {
      // Deep clone the current form data as the original state
      this.originalFormData = {
        core_metrics: JSON.parse(JSON.stringify(this.formData.core_metrics)),
        description_metrics: JSON.parse(JSON.stringify(this.formData.description_metrics)),
        optional_attributes: JSON.parse(JSON.stringify(this.formData.optional_attributes)),
        evaluator_comments: this.formData.evaluator_comments
      }
      this.originalActiveOptionalAttributes = [...this.activeOptionalAttributes]
    },

    hasChanges() {
      if (!this.originalFormData) {
        // No original data means this is a new score, so treat as changed
        return true
      }

      // Check if active optional attributes changed
      if (this.activeOptionalAttributes.length !== this.originalActiveOptionalAttributes.length) {
        return true
      }
      if (!this.activeOptionalAttributes.every(attr => this.originalActiveOptionalAttributes.includes(attr))) {
        return true
      }

      // Check core_metrics
      if (JSON.stringify(this.formData.core_metrics) !== JSON.stringify(this.originalFormData.core_metrics)) {
        return true
      }

      // Check description_metrics
      if (JSON.stringify(this.formData.description_metrics) !== JSON.stringify(this.originalFormData.description_metrics)) {
        return true
      }

      // Check optional_attributes
      if (JSON.stringify(this.formData.optional_attributes) !== JSON.stringify(this.originalFormData.optional_attributes)) {
        return true
      }

      // Check comments
      if (this.formData.evaluator_comments !== this.originalFormData.evaluator_comments) {
        return true
      }

      return false
    },

    formatLabel(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    },

    setBoolean(section, key, value) {
      this.$set(this.formData[section], key, value)
    },

    setEnum(section, key, value) {
      this.$set(this.formData[section], key, value)
    },

    addOptionalAttribute(key) {
      if (!this.activeOptionalAttributes.includes(key)) {
        this.activeOptionalAttributes.push(key)
        // Initialize with null or default based on static field definition
        const def = this.optionalAttributesFields[key]
        if (def && def.type === 'integer') {
          this.$set(this.formData.optional_attributes, key, def.minimum || 0)
        } else {
          this.$set(this.formData.optional_attributes, key, null)
        }
      }
    },

    removeOptionalAttribute(key) {
      const idx = this.activeOptionalAttributes.indexOf(key)
      if (idx !== -1) {
        this.activeOptionalAttributes.splice(idx, 1)
        this.$delete(this.formData.optional_attributes, key)
      }
    },

    async toggleEditMode() {
      if (this.editMode) {
        // Check if there are any changes before saving
        if (!this.hasChanges()) {
          // No changes made, just exit edit mode
          this.editMode = false
          this.saveError = null
          this.missingFields = []
          return
        }
        // Save mode - there are changes
        await this.saveScore()
      } else {
        // Enter edit mode
        this.editMode = true
        this.saveError = null
        this.missingFields = []
      }
    },

    cancelChanges() {
      if (this.originalFormData) {
        this.formData = JSON.parse(JSON.stringify(this.originalFormData))
        this.activeOptionalAttributes = [...this.originalActiveOptionalAttributes]
      } else {
        this.initializeFormData()
      }
      this.editMode = false
      this.saveError = null
      this.missingFields = []
    },

    clearScore() {
      // Show confirmation dialog
      if (confirm('Are you sure you want to clear this score? This action cannot be undone.')) {
        this.performClear()
      }
    },

    async performClear() {
      this.saving = true
      this.saveError = null
      this.missingFields = []

      try {
        // Delete the score
        await service.deleteScore(this.jobId, this.model)
        this.existingScore = null
        this.initializeFormData()
        this.editMode = false
        this.storeOriginalState()
      } catch (e) {
        console.error('Error clearing score:', e)
        this.saveError = service.formatError ? service.formatError(e) : 'Failed to clear score'
      } finally {
        this.saving = false
      }
    },

    async saveScore() {
      // Validation: Check if all core metrics are filled
      this.missingFields = []
      for (const fieldKey in this.formData.core_metrics) {
        if (this.formData.core_metrics[fieldKey] === null || this.formData.core_metrics[fieldKey] === undefined) {
          this.missingFields.push(fieldKey)
        }
      }
      if (this.missingFields.length > 0) {
        this.saveError = `Please fill in all required core metrics: ${this.missingFields.map(k => this.formatLabel(k)).join(', ')}`
        return
      }

      this.saving = true
      this.saveError = null

      try {
        // Build the score data
        const scoreData = {
          core_metrics: this.formData.core_metrics,
          metadata: {
            job_id: this.jobId,
            s3_key: this.s3Key,
            model: this.model
          }
        }

        const descriptionMetrics = {}
        for (const key of Object.keys(this.formData.description_metrics)) {
          if (this.formData.description_metrics[key] !== null && this.formData.description_metrics[key] !== undefined) {
            descriptionMetrics[key] = this.formData.description_metrics[key]
          }
        }
        if (Object.keys(descriptionMetrics).length > 0) {
          scoreData.description_metrics = descriptionMetrics
        }

        // Only include optional_attributes if there are any
        if (this.activeOptionalAttributes.length > 0) {
          const optAttrs = {}
          for (const key of this.activeOptionalAttributes) {
            if (this.formData.optional_attributes[key] !== null && this.formData.optional_attributes[key] !== undefined) {
              optAttrs[key] = this.formData.optional_attributes[key]
            }
          }
          if (Object.keys(optAttrs).length > 0) {
            scoreData.optional_attributes = optAttrs
          }
        }

        // Include comments if present
        if (this.formData.evaluator_comments) {
          scoreData.evaluator_comments = this.formData.evaluator_comments
        }

        await service.saveScore(this.jobId, this.model, scoreData)
        
        // Success - update existing score
        this.existingScore = { ...scoreData }
        this.editMode = false
        this.storeOriginalState()
      } catch (e) {
        console.error('Error saving score:', e)
        this.saveError = service.formatError ? service.formatError(e) : 'Failed to save score'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.scoring-panel-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.scoring-header {
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
}

.panel-title {
  font-weight: bold;
  text-transform: lowercase;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

.scoring-form {
  flex: 1;
  overflow-y: auto;
}

.section-header {
  font-size: 0.9rem;
  color: #333;
}

.field-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  flex-wrap: wrap;
}

.field-row.missing-field {
  border-color: #dc3545;
  background-color: #f8d7da;
}

.field-label {
  flex: 0 0 55%;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
}

.field-input {
  flex: 0 0 42%;
  display: flex;
  justify-content: flex-end;
}

.tooltip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 10px;
  background: #e0e0e0;
  border-radius: 50%;
  cursor: help;
  color: #666;
}

.remove-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 14px;
  color: #dc3545;
  cursor: pointer;
  margin-left: 0.25rem;
}

.remove-icon:hover {
  color: #a71d2a;
}

.toggle-boxes {
  display: flex;
  gap: 4px;
}

.toggle-box {
  padding: 4px 10px;
  font-size: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  background: #fff;
  user-select: none;
  transition: all 0.15s;
}

.toggle-box:hover:not(.disabled) {
  border-color: #007bff;
}

.toggle-box.selected {
  background: #007bff;
  color: #fff;
  border-color: #007bff;
}

.toggle-box.disabled {
  cursor: default;
  opacity: 0.7;
}

.range-container {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.form-range {
  flex: 1;
  height: 6px;
}

.range-value {
  min-width: 24px;
  text-align: center;
  font-weight: bold;
  font-size: 0.9rem;
}

.form-control-sm {
  max-width: 100px;
}

textarea.form-control {
  font-size: 0.85rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .field-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .field-label {
    flex: 1 1 100%;
    margin-bottom: 0.5rem;
  }

  .field-input {
    flex: 1 1 100%;
    justify-content: flex-start;
    margin-top: 0.25rem;
  }

  .range-container {
    width: 100%;
  }

  .form-control-sm {
    max-width: none;
    width: 100%;
  }
}
</style>
